import { Autocomplete, Box, IconButton, Link, TextField } from "@mui/material";
import ArrowDropDown from "@mui/icons-material/ArrowDropDown";
import { useAsync2 } from "nate-react-api-helpers";
import { useState } from "react";
import { api } from "../../../api/API";
import { findColor, projectColorText, randomColor } from "../../../misc/projectColors";
import { useSnackAction } from "../../../misc/SnackAction";

// ProjectRef is one entry of the dropdown, flattened to what it takes to draw a row
// and to label the field. A goal carries the project it sits under, since filing a
// task under a goal files it under that project too.
export type ProjectRef = {
    id: number;
    name: string;
    color: string;

    // goal is 0 for the project itself, and the goal's id for one of its pieces
    goal: number;

    // goalName names that piece, and is empty for the project itself
    goalName: string;
}

// an id of 0 is the "create a project by this name" entry, which lets a task be filed
// under a new project without leaving the panel
const createId = 0;

// Option is a ProjectRef placed in the dropdown's tree, which needs to know where the
// row sits to draw the branch leading to it
type Option = ProjectRef & {
    // lastGoal marks the bottom goal of a project, where the branch line stops
    lastGoal: boolean;
}

// TaskProject files the task under the project it works towards, or under one of that
// project's goals. Once filed, the field reads as the trail to what it's filed under,
// so the project and the goal are each a step away.
export function TaskProject(props: {
    // team is the task's; only its projects are offered
    team: number;

    value: number | null;
    goal: number | null;
    name: string;
    goalName: string;
    color: string;

    // onChange carries enough of the choice to label the field until the task is saved
    // and the server answers with the rest
    onChange: (project: ProjectRef | null) => void;

    // onOpen swaps the panel over to the project or goal the task is filed under.
    // Without it the two are named but lead nowhere, which is how a task that hasn't
    // been saved yet reads.
    onOpen?: (ref: { project: number; goal: number }) => void;
}) {
    const [search, setSearch] = useState("");
    const [editing, setEditing] = useState(false);

    const options = useAsync2(input => api.projects.list(input),
        { search: search, archived: false, team: props.team || undefined }, [search, props.team]);

    // each project is followed by its goals, so picking either files the task
    const available: Option[] = (options.asList || []).flatMap(project => {
        const goals = project.goals.filter(goal => !goal.archived);

        return [
            { id: project.id, name: project.name, color: project.color, goal: 0, goalName: "", lastGoal: false },
            ...goals.map((goal, i) => ({
                id: project.id,
                name: project.name,
                color: project.color,
                goal: goal.id,
                goalName: goal.name,
                lastGoal: i === goals.length - 1,
            })),
        ];
    });

    const selected: Option | null = props.value
        ? {
            id: props.value,
            name: props.name,
            color: props.color,
            goal: props.goal || 0,
            goalName: props.goalName,
            lastGoal: false,
        }
        : null;

    const create = useSnackAction(async (input: { name: string }) => {
        const created = await api.projects.upsert({ name: input.name, color: randomColor(), team: props.team });
        props.onChange({ id: created.id, name: created.name, color: created.color, goal: 0, goalName: "" });
    }, { success: "Project created" });

    const color = findColor(props.color);

    if (selected && !editing && !create.loading) {
        return (
            <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
                <Box sx={{
                    display: "flex", alignItems: "center", gap: 0.5, minWidth: 0,
                    px: 0.75, borderRadius: 1,
                    fontSize: "0.8125rem",
                    bgcolor: color.bg, color: projectColorText(),
                }}>
                    <Step name={selected.name}
                          onOpen={props.onOpen && (() => props.onOpen?.({
                              project: selected.id, goal: 0,
                          }))} />
                    {selected.goal > 0 && (
                        <>
                            <Box component="span" sx={{ opacity: 0.6 }}>›</Box>
                            <Step name={selected.goalName}
                                  onOpen={props.onOpen && (() => props.onOpen?.({
                                      project: selected.id, goal: selected.goal,
                                  }))} />
                        </>
                    )}
                    <IconButton size="small" title="Change project" aria-label="Change project"
                                sx={{ color: "inherit", p: 0, ml: 0.25 }}
                                onClick={() => setEditing(true)}>
                        <ArrowDropDown sx={{ fontSize: 18 }} />
                    </IconButton>
                </Box>
            </Box>
        );
    }

    return (
        <>
            <Autocomplete
                blurOnSelect
                autoHighlight
                openOnFocus
                size="small"
                disabled={create.loading}
                options={available}
                value={selected}
                onInputChange={(_, value, reason) => {
                    // "reset" is the box being re-labelled with the current choice,
                    // which isn't a search the user asked for
                    if (reason === "reset") return;

                    setSearch(value);
                }}
                isOptionEqualToValue={(a: Option, b: Option) =>
                    a.id === b.id && a.goal === b.goal}
                // a goal reads as the trail to it, so the row names what it belongs to
                getOptionLabel={(option: Option) =>
                    option.goal ? option.name + " › " + option.goalName : option.name}
                // the server already searched; the create option leads so enter takes it
                filterOptions={list => {
                    const name = search.trim();
                    if (!name) return list;

                    return ([{ id: createId, name: name, color: "", goal: 0, goalName: "", lastGoal: false }] as Option[])
                        .concat(list);
                }}
                renderOption={(params, option: Option) => {
                    const { key, ...rest } = params as typeof params & { key: string };
                    const swatch = findColor(option.color);

                    if (option.id === createId) {
                        return <Box component="li" key={key} {...rest} sx={{ gap: 1 }}>
                            {"Create: " + option.name}
                        </Box>;
                    }

                    // a goal hangs off the project above it on a branch line, so the
                    // rows read as a tree rather than as one flat list
                    if (option.goal) {
                        return (
                            // the listbox styles every option's padding, so the row
                            // only gets its own by taking it back
                            <Box component="li" key={key} {...rest}
                                 sx={{ gap: 1, py: "0 !important", pl: "12px !important" }}>
                                <Branch last={option.lastGoal} />
                                <Box sx={{
                                    py: 0.75, minWidth: 0, overflow: "hidden",
                                    textOverflow: "ellipsis", whiteSpace: "nowrap",
                                    color: "text.secondary",
                                }}>
                                    {option.goalName}
                                </Box>
                            </Box>
                        );
                    }

                    return (
                        <Box component="li" key={key} {...rest} sx={{ gap: 1, fontWeight: 500 }}>
                            <Box sx={{
                                width: 12, height: 12, borderRadius: "50%", flexShrink: 0,
                                bgcolor: swatch.bg, border: 1, borderColor: swatch.border,
                            }} />
                            <Box sx={{
                                minWidth: 0, overflow: "hidden",
                                textOverflow: "ellipsis", whiteSpace: "nowrap",
                            }}>
                                {option.name}
                            </Box>
                        </Box>
                    );
                }}
                // leaving the field goes back to the trail, so the panel isn't left
                // showing a search box over what the task is already filed under
                onBlur={() => setEditing(false)}
                onChange={(_, value: ProjectRef | null) => {
                    setSearch("");
                    setEditing(false);

                    if (!value) {
                        props.onChange(null);
                        return;
                    }

                    if (value.id === createId) {
                        create.callback({ name: value.name });
                        return;
                    }

                    props.onChange(value);
                }}
                renderInput={params => (
                    <TextField
                        {...params}
                        variant="standard"
                        autoFocus={editing}
                        placeholder="File under a project or goal, or type a new project"
                        sx={{ "& .MuiAutocomplete-input": { py: 0.25 } }}
                        slotProps={{
                            input: {
                                ...params.InputProps,
                                disableUnderline: true,
                                style: {
                                    fontSize: "0.8125rem",
                                    borderRadius: 4,
                                    paddingLeft: 6,
                                    ...(props.value ? {
                                        backgroundColor: color.bg,
                                        color: projectColorText(),
                                    } : {}),
                                },
                            },
                        }}
                    />
                )}
            />
            {options.LoadingOrErrorElement}
        </>
    );
}

// Branch is the line drawn from a project down to the goal on the row, and on down to
// the goals below it while there are any left
function Branch(props: { last: boolean }) {
    return (
        <Box sx={{ width: 16, flexShrink: 0, position: "relative", alignSelf: "stretch" }}>
            <Box sx={{
                position: "absolute", left: 0, top: 0,
                bottom: props.last ? "50%" : 0,
                borderLeft: 1, borderColor: "divider",
            }} />
            <Box sx={{
                position: "absolute", left: 0, top: "50%", width: 10,
                borderTop: 1, borderColor: "divider",
            }} />
        </Box>
    );
}

// Step is one part of what the task is filed under, which leads to that project or
// goal where there's a panel to show it in
function Step(props: { name: string; onOpen?: () => void }) {
    if (!props.onOpen) {
        return <Box component="span" sx={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {props.name}
        </Box>;
    }

    return (
        <Link component="button" type="button" variant="inherit"
              color="inherit" underline="hover" noWrap
              onClick={props.onOpen}>
            {props.name}
        </Link>
    );
}
